import { LegacyProductCode, ProductsApi } from '@up2stay/up2promo-api-client';
import type {
  ClaimMethod,
  ProductHashResolutionKind as ServerResolutionKind,
} from '@up2stay/up2promo-api-client';
import { behaviorTypeToProductType } from '@up2stay/up2promo-commons/products';

/**
 * What the member can do with a scanned hash. The classification is
 * server-side (`ProductsApi.resolveProductHash`, DEV-753); `error` is the
 * client's own outcome when the request fails.
 */
export type ProductHashResolutionKind = `${ServerResolutionKind}` | 'error';

export type ProductHashResolution = {
  kind: ProductHashResolutionKind;
  hash: string;
  productType?: string;
  productId?: string;
  /** `claim_pending` only. */
  claimMethod?: ClaimMethod;
};

/**
 * Determines what the user should do after scanning a product code in the
 * current workspace. Merges former "add" (setup) and "claim" availability
 * checks.
 */
export async function resolveProductHash(
  hash: string,
  workspaceId: string,
): Promise<ProductHashResolution> {
  const trimmed = hash.trim();
  if (!trimmed) {
    return { kind: 'error', hash: trimmed };
  }

  try {
    const { data } = await ProductsApi.resolveProductHash({
      path: { workspaceId, hash: trimmed },
    });
    // Factory batches only store the item behaviour (ADR-0014); a scan
    // without `?t=` still knows the product type (DEV-456). Generic URL
    // (CU) products stay open for the select-type pick.
    const behaviorType = data.behavior
      ? behaviorTypeToProductType(data.behavior)
      : undefined;
    const productType =
      behaviorType !== LegacyProductCode.CU ? behaviorType : undefined;
    return {
      kind: data.kind,
      hash: trimmed,
      ...(productType ? { productType } : {}),
      ...(data.productId ? { productId: data.productId } : {}),
      ...(data.claimMethod ? { claimMethod: data.claimMethod } : {}),
    };
  } catch {
    return { kind: 'error', hash: trimmed };
  }
}

export function isBlockingResolution(kind: ProductHashResolutionKind): boolean {
  return kind !== 'ready_for_setup';
}
